import { SmsPreview } from './SmsPreview';
import { useSmsPreview } from '../useSmsPreview';
import csMessages from '@shared/locales/cs.json';
import { useSmsAutomationAccess } from '../useSmsAutomationAccess';
import { estimateSms } from '../smsEstimate';
import { smsMoney, useSmsSummary } from '../smsCredit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Typography,
  Chip,
  Stack,
  Switch,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from '@mui/material';
import { IntlShape, useIntl } from 'react-intl';
import {
  SmsTemplateType,
  type CreateOrUpdateSmsTemplateRequest,
  type SmsTemplate,
  DEFAULT_SMS_TEMPLATES,
} from '../smsTemplates';
import {
  useCreateOrUpdateSmsTemplate,
  useSmsPlaceholders,
  useSmsTemplates,
} from '../useSmsTemplates';

type SmsTemplateEditorProps = {
  open: boolean;
  onClose: () => void;
  template?: SmsTemplate;
};

const getTemplateTypeLabel = (templateType: SmsTemplateType, intl: IntlShape) => {
  const TEMPLATE_TYPE_LABELS: Record<SmsTemplateType, string> = {
    [SmsTemplateType.DEPOSIT_REMINDER]: intl.formatMessage({
      id: 'smsTemplate.type.depositReminder',
      defaultMessage: 'Připomenutí zálohy',
    }),
    [SmsTemplateType.APPOINTMENT_REMINDER]: intl.formatMessage({
      id: 'smsTemplate.type.appointmentReminder',
      defaultMessage: 'Připomenutí schůzky',
    }),
    [SmsTemplateType.REVIEW_REQUEST]: intl.formatMessage({
      id: 'smsTemplate.type.reviewRequest',
      defaultMessage: 'Žádost o recenzi',
    }),
    // [SmsTemplateType.APPOINTMENT_CONFIRMATION]: {
    //   id: 'smsTemplate.type.appointmentConfirmation',
    //   defaultMessage: 'Potvrzení schůzky',
    // },
    // [SmsTemplateType.CANCELLATION_NOTICE]: {
    //   id: 'smsTemplate.type.cancellationNotice',
    //   defaultMessage: 'Oznámení o zrušení',
    // },
  };

  return TEMPLATE_TYPE_LABELS[templateType];
};

// Helper function to get translation key for placeholder
const getPlaceholderTranslationKey = (placeholder: string): string => {
  const placeholderName = placeholder.replace(/[{}]/g, ''); // Remove braces
  return `smsPlaceholder.${placeholderName}`;
};

export const SmsTemplateEditor = (props: SmsTemplateEditorProps) => {
  const renderPreview = useSmsPreview();
  const smsAutomationEnabled = useSmsAutomationAccess();
  const { t } = useIntlTranslations();
  const { open, onClose, template } = props;
  const textInput = useRef<HTMLTextAreaElement>(null);
  const { data: summary } = useSmsSummary();
  const intl = useIntl();
  const [type, setType] = useState<SmsTemplateType>(SmsTemplateType.DEPOSIT_REMINDER);
  const [content, setContent] = useState('');
  const [automatic, setAutomatic] = useState(false);
  const [daysBefore, setDaysBefore] = useState(1);
  const [sendTime, setSendTime] = useState('09:00');
  const { mutate: saveTemplate, isPending } = useCreateOrUpdateSmsTemplate();
  const { data: placeholdersData } = useSmsPlaceholders();
  const { data: templatesData } = useSmsTemplates();
  const existingTypes = (templatesData?.templates ?? []).map(t => t.type);
  const availableTypes = template
    ? Object.values(SmsTemplateType)
    : Object.values(SmsTemplateType).filter(t => !existingTypes.includes(t));

  // Synchronizuj state s props template když se dialog otevře nebo template změní
  useEffect(() => {
    if (open) {
      setAutomatic(template?.automatic ?? false);
      setDaysBefore(template?.daysBefore ?? 1);
      setSendTime(template?.sendTime ?? '09:00');
      if (template) {
        // Editace existující šablony
        setType(template.type);
        setContent(template.content);
      } else {
        // Vytváření nové šablony - nastav výchozí hodnoty
        const first = availableTypes[0] ?? SmsTemplateType.APPOINTMENT_REMINDER;
        setType(first);
        setContent(DEFAULT_SMS_TEMPLATES[first]);
      }
    }
  }, [open, template]);

  // Když uživatel změní typ u NOVÉ šablony, aktualizuj obsah na výchozí
  useEffect(() => {
    if (open && !template) {
      setContent(DEFAULT_SMS_TEMPLATES[type]);
    }
  }, [type, open, template]);

  const handleSave = () => {
    const data: CreateOrUpdateSmsTemplateRequest = {
      type,
      content,
      ...(smsAutomationEnabled
        ? {
            automatic: type === SmsTemplateType.APPOINTMENT_REMINDER && automatic,
            daysBefore,
            sendTime,
          }
        : {}),
    };

    saveTemplate(data, {
      onSuccess: () => {
        onClose();
      },
    });
  };

  const insertPlaceholder = (placeholder: string) => {
    const input = textInput.current;
    const start = input?.selectionStart ?? content.length;
    const end = input?.selectionEnd ?? content.length;
    setContent(content.slice(0, start) + placeholder + content.slice(end));
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + placeholder.length, start + placeholder.length);
    });
  };

  const currentPlaceholders = placeholdersData?.placeholders[type] || [];
  const isCreatingNew = !template;

  const isAutomatic =
    smsAutomationEnabled && type === SmsTemplateType.APPOINTMENT_REMINDER && automatic;
  const example = renderPreview(content);
  const { parts, units, unicode } = estimateSms(example);
  const estimatedPrice = summary ? smsMoney(parts * summary.priceHalers) : '—';
  const m = (id: string, values?: Record<string, string | number>) => {
    const key = `smsEditor.${id}`;
    return intl.formatMessage(
      { id: key, defaultMessage: csMessages[key as keyof typeof csMessages] },
      values,
    );
  };
  const invalidSchedule =
    isAutomatic &&
    (!Number.isInteger(daysBefore) ||
      daysBefore < 1 ||
      daysBefore > 30 ||
      !/^(0[8-9]|1[0-9]|20):[0-5][0-9]$/.test(sendTime));
  const cardSx = { p: { xs: 2, sm: 2.5 }, border: 1, borderColor: 'divider', borderRadius: 3 };

  return (
    <Dialog
      open={open}
      onClose={isPending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="sms-editor-title"
      slotProps={{
        paper: {
          sx: {
            borderRadius: { xs: 3, sm: 4 },
            m: { xs: 1, sm: 3 },
            width: { xs: 'calc(100% - 16px)', sm: '100%' },
            maxHeight: 'calc(100% - 32px)',
          },
        },
      }}
    >
      <DialogTitle
        id="sms-editor-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: { xs: 2, sm: 3 },
          pt: 3,
          fontWeight: 800,
        }}
      >
        <IconButton onClick={onClose} disabled={isPending} aria-label={t('common.cancel')}>
          <ArrowBackIcon />
        </IconButton>
        {template ? m('editTitle') : m('createTitle')}
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
        <Stack spacing={3} sx={{ pt: 1 }}>
          {isCreatingNew ? (
            <FormControl fullWidth size="small">
              <InputLabel id="template-type-label">{t('smsTemplate.type')}</InputLabel>
              <Select
                labelId="template-type-label"
                value={type}
                label={t('smsTemplate.type')}
                onChange={e => setType(e.target.value as SmsTemplateType)}
              >
                {availableTypes.map(templateType => (
                  <MenuItem key={templateType} value={templateType}>
                    {getTemplateTypeLabel(templateType, intl)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box>
              <Chip
                label={getTemplateTypeLabel(type, intl)}
                sx={{
                  bgcolor: 'primary.light',
                  color: 'primary.main',
                  fontWeight: 700,
                  border: 1,
                  borderColor: 'primary.main',
                }}
              />
            </Box>
          )}

          <Box aria-live="polite">
            <Typography variant="body2" fontWeight={700}>
              {m('deliveryMode', { mode: m(isAutomatic ? 'automatic' : 'manual') })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {m(isAutomatic ? 'automaticHint' : 'manualHint')}
            </Typography>
          </Box>

          {smsAutomationEnabled && type === SmsTemplateType.APPOINTMENT_REMINDER && (
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-around"
                sx={{ ...cardSx, py: 1 }}
              >
                <Typography fontWeight={!automatic ? 700 : 400}>{m('manual')}</Typography>
                <Switch
                  checked={automatic}
                  onChange={e => setAutomatic(e.target.checked)}
                  slotProps={{ input: { 'aria-label': t('smsTemplate.automaticDelivery') } }}
                />
                <Typography
                  fontWeight={automatic ? 700 : 400}
                  color={automatic ? 'primary.main' : 'text.secondary'}
                >
                  {m('automatic')}
                </Typography>
              </Stack>
              {automatic && (
                <Stack spacing={1}>
                  <Stack direction="row" gap={2} flexWrap="wrap">
                    <TextField
                      size="small"
                      sx={{ flex: '1 1 140px' }}
                      label={t('smsTemplate.daysBefore')}
                      type="number"
                      value={daysBefore}
                      onChange={e => setDaysBefore(Number(e.target.value))}
                      slotProps={{ htmlInput: { min: 1, max: 30, step: 1 } }}
                    />
                    <TextField
                      size="small"
                      sx={{ flex: '1 1 140px' }}
                      label={t('smsTemplate.sendTime')}
                      type="time"
                      value={sendTime}
                      onChange={e => setSendTime(e.target.value)}
                      slotProps={{
                        inputLabel: { shrink: true },
                        htmlInput: { min: '08:00', max: '20:59' },
                      }}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {m('scheduleHint')}
                  </Typography>
                </Stack>
              )}
            </Stack>
          )}

          <Box sx={cardSx}>
            <Typography fontWeight={700} gutterBottom>
              {m('priceTitle')}
            </Typography>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              flexWrap="wrap"
            >
              <Typography variant={isAutomatic ? 'h4' : 'h6'} fontWeight={800}>
                {isAutomatic ? estimatedPrice : m('operatorPrice')}
              </Typography>
              <Chip
                size="small"
                color={parts > 1 ? 'warning' : 'default'}
                label={m('parts', { count: parts })}
                sx={{ fontWeight: 700, borderRadius: 2 }}
              />
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {isAutomatic
                ? summary
                  ? `${parts} × ${smsMoney(summary.priceHalers)}`
                  : m('priceUnavailable')
                : m('noCredit')}
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <InfoOutlinedIcon
                color={isAutomatic && parts > 1 ? 'warning' : 'action'}
                fontSize="small"
              />
              <Typography variant="body2" color="text.secondary">
                {isAutomatic ? m('chargedParts') : m('joinedParts')}
              </Typography>
            </Stack>
          </Box>

          <Box sx={{ isolation: 'isolate' }}>
            <Typography fontWeight={700} sx={{ mb: 1.5 }}>
              {m('previewTitle')}
            </Typography>
            <SmsPreview content={example || m('emptyPreview')} />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {m('previewHint')}
            </Typography>
          </Box>

          <Stack spacing={1.5}>
            <TextField
              inputRef={textInput}
              label={m('templateText')}
              multiline
              minRows={4}
              maxRows={10}
              value={content}
              onChange={e => setContent(e.target.value)}
              fullWidth
              error={content.length > 1500}
              helperText={m('textLength', { count: content.length })}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 3 },
                '& textarea': { lineHeight: 1.7 },
              }}
            />
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {currentPlaceholders.map(p => (
                <Chip
                  key={p.placeholder}
                  icon={<AddIcon />}
                  label={intl.formatMessage({
                    id: getPlaceholderTranslationKey(p.placeholder),
                    defaultMessage: p.description,
                  })}
                  onClick={() => insertPlaceholder(p.placeholder)}
                  color="primary"
                  variant="outlined"
                  sx={{ py: 2.25, borderRadius: 5 }}
                />
              ))}
            </Stack>
          </Stack>

          <Box sx={{ ...cardSx, bgcolor: 'success.light', borderColor: 'success.light' }}>
            <Typography fontWeight={700} gutterBottom>
              {m('tipsTitle')}
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              {['tipShorten', 'tipDiacritics', 'tipDetails'].map(key => (
                <Stack key={key} direction="row" gap={1.5} alignItems="center">
                  <LightbulbOutlinedIcon color="success" fontSize="small" />
                  <Typography variant="body2">{m(key)}</Typography>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: '12px !important',
              '&::before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="sms-price-explanation"
              id="sms-price-explanation-title"
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <InfoOutlinedIcon color="action" fontSize="small" />
                <Typography>{m('explanationTitle')}</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails id="sms-price-explanation">
              <Stack spacing={1.5}>
                <Typography variant="body2" color="text.secondary">
                  {t(unicode ? 'smsPrice.unicode' : 'smsPrice.gsm', { units })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isAutomatic ? m('gatewayEstimate') : t('smsPrice.manual')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {m('previewHint')}
                </Typography>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          justifyContent: 'space-between',
          gap: 2,
          boxShadow: '0 -6px 24px rgba(0,0,0,0.04)',
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            {isAutomatic ? m('footerEstimate') : m('footerCredit')}
          </Typography>
          <Typography fontWeight={800} fontSize={20}>
            {isAutomatic ? `${estimatedPrice} · ${parts} SMS` : smsMoney(0)}
          </Typography>
        </Box>
        <Button
          onClick={handleSave}
          variant="contained"
          size="large"
          sx={{ borderRadius: 2.5, px: 3, py: 1.5, boxShadow: 'none' }}
          disabled={isPending || !content.trim() || content.length > 1500 || invalidSchedule}
        >
          {isPending ? m('saving') : m('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
